import { animated, config, useSpring } from '@react-spring/web'
import { useEffect, useState } from 'react'

import styled from 'styled-components'

const getReturnValues = (countDown: number) => {
  const days = Math.floor(countDown / (1000 * 60 * 60 * 24))
  const hours = Math.floor((countDown % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((countDown % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((countDown % (1000 * 60)) / 1000)

  return [days, hours, minutes, seconds]
}

const getDate = (epoch: number) => {
  const date = new Date(epoch)
  return date
}

const useCountdown = (epoch: number) => {
  const date = getDate(epoch * 1000)
  const countDownDate = date.getTime()

  const [countDown, setCountDown] = useState(Math.max(0, countDownDate - new Date().getTime()))

  useEffect(() => {
    if (!epoch) {
      return
    }
    const interval = setInterval(() => {
      setCountDown(Math.max(0, countDownDate - new Date().getTime()))
    }, 1000)

    return () => clearInterval(interval)
  }, [countDownDate, epoch])

  return getReturnValues(countDown)
}

function MiddleTimer({
  timeStamp,
  unrevealedAmountOfGobblers,
}: {
  timeStamp: number
  unrevealedAmountOfGobblers?: number | undefined
}) {
  const [_, hour, minutes, seconds] = useCountdown(timeStamp ?? 0)

  const styles = useSpring({
    from: { opacity: 0, transform: 'translateY(17.5%)', scale: 0.95 },
    to: { opacity: 1, transform: 'translateY(0%)', scale: 1 },
    delay: 1000,
    config: config.slow,
  })
  const othersText = unrevealedAmountOfGobblers === 1 ? 'other' : 'others'
  return (
    <AnimatedMiddleTimerContainer
      style={styles}
      unrevealedAmountOfGobblers={unrevealedAmountOfGobblers}
    >
      <OpensInDiv>Arrives in</OpensInDiv>
      <TimerText>
        <>
          {hour}
          <TimerSpan>h</TimerSpan> {minutes}
          <TimerSpan>m</TimerSpan> {seconds}
          <TimerSpan>s</TimerSpan>
        </>
      </TimerText>
      {!!unrevealedAmountOfGobblers && (
        <UnrevealedDiv>
          {unrevealedAmountOfGobblers} {othersText} waiting
        </UnrevealedDiv>
      )}
    </AnimatedMiddleTimerContainer>
  )
}

const MiddleTimerContainer = styled.div<{
  unrevealedAmountOfGobblers?: number | undefined
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  width: 375px;
  height: ${(props) => (props.unrevealedAmountOfGobblers ? '182px' : '135px')};

  background: rgba(8, 10, 12, 0.7);
  backdrop-filter: blur(5px);

  border-radius: 40px;
  z-index: 7;

  ${({ theme }) => theme.mediaWidth.upToSmall`
  width: 282px;
  `};
`

const AnimatedMiddleTimerContainer = animated(MiddleTimerContainer)

const OpensInDiv = styled.div`
  font-size: 18px;
  font-weight: 600;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    font-size: 14px;
  `};
`

const UnrevealedDiv = styled.div`
  font-size: 18px;
  font-weight: 400;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    font-size: 14px;
  `};
`

const TimerText = styled.div`
  background: #080a0c;
  width: calc(100% - 16px);
  height: 72px;
  border-radius: 32px;

  box-shadow: 0px 1px 1px rgba(255, 255, 255, 0.12), inset 1px 2px 5px rgba(0, 0, 0, 0.32);

  font-size: 36px;
  font-weight: 700;
  display: flex;
  justify-content: center;
  align-items: center;
  font-variant-numeric: tabular-nums;
`

const TimerSpan = styled.span`
  color: #b8bccf;
  margin-right: 8px;
`

export default MiddleTimer
